import { usePropertyServicePropertyRisksListKey } from '@insurance/api/queries'
import { PropertyService } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { Form, Select, SelectProps } from 'antd'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CategoryType } from './type'
import { CATEGORIES, formProps } from './utils'

type Props = {
  fieldKey: number
} & SelectProps

export const ItemCategoriesSelect = ({ fieldKey, ...props }: Props) => {
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const { ItemCategoryProps, itemsKey, ItemRisksProps } = formProps(t)
  const form = Form.useFormInstance()
  const queryClient = useQueryClient()
  const items = Form.useWatch(itemsKey.name)
  const currentCategory = Form.useWatch([
    itemsKey.name,
    fieldKey,
    ItemCategoryProps.name
  ])

  const selectedCategories = useMemo(() => {
    const selected = new Set<string>()
    const itemsArray = items || []
    itemsArray.forEach((item: Record<string, unknown>, index: number) => {
      if (index !== fieldKey && item?.[ItemCategoryProps.name]) {
        selected.add(item[ItemCategoryProps.name] as string)
      }
    })
    return selected
  }, [items, fieldKey, ItemCategoryProps.name])

  const categoryOptions = useMemo(() => {
    return CATEGORIES.filter(
      (value) => !selectedCategories.has(value) || value === currentCategory
    ).map((value) => ({
      label: t(`item_category_${value}`),
      value
    }))
  }, [selectedCategories, currentCategory, t])

  const onChangeCategory = useCallback(
    async (value?: string) => {
      form.setFieldValue([fieldKey, ItemCategoryProps.name], value)

      if (value) {
        const categoryUpper = value.toUpperCase() as CategoryType

        const risksData = await queryClient.fetchQuery({
          queryKey: [
            usePropertyServicePropertyRisksListKey,
            [{ category: categoryUpper }]
          ],
          queryFn: () => PropertyService.propertyRisksList(categoryUpper)
        })

        const filteredRisks = risksData?.filter(
          (item) => item.type?.value !== 'full'
        )

        form.setFieldValue(
          [itemsKey.name, fieldKey, ItemRisksProps.name],
          filteredRisks?.map((item) => item.id)
        )
      } else {
        form.setFieldValue(
          [itemsKey.name, fieldKey, ItemRisksProps.name],
          undefined
        )
      }
    },
    [
      form,
      fieldKey,
      ItemCategoryProps.name,
      itemsKey.name,
      ItemRisksProps.name,
      queryClient
    ]
  )

  return (
    <Form.Item
      {...ItemCategoryProps}
      name={[fieldKey, ItemCategoryProps.name]}
      rules={[required]}
    >
      <Select
        placeholder={ItemCategoryProps.label}
        options={categoryOptions}
        onChange={onChangeCategory}
        {...props}
      />
    </Form.Item>
  )
}
